import { isEmpty } from 'lodash/fp'
import { logger } from 'helpers/logger'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(duration)

const oneMinute = 60 * 1000
const oneHour = oneMinute * 60
const oneDay = oneHour * 24
const timeValues = [oneDay, oneHour, oneMinute]

const timeListToString = (list: number[]) =>
  list.map((time) => (time ? timeToString(time) : '00')).join(':')

export const timeToString = (time: number) =>
  time < 10 ? `0${time}` : time.toString()

const findTimeRange = (list: number[]) =>
  list.reduce((acc, time) => (time || !isEmpty(acc) ? [...acc, time] : acc), [])

const getTimeListInMilliseconds = (timeStamp: number) =>
  timeValues.reduce(
    (acc, timeValue, index) => [...acc, acc[index] % timeValue],
    [timeStamp],
  )

const getTimeList = (timeStampList: number[]) =>
  timeStampList.reduce(
    (acc, timeStamp, index) => [
      ...acc,
      Math.floor(timeStamp / (timeValues[index] || 1000)),
    ],
    [],
  )

type FormatTimeSettings = { onlyMin?: boolean }

const formatTime = (
  timeInSeconds: number,
  { onlyMin }: FormatTimeSettings = {},
) => {
  if (timeInSeconds <= 0) return '0:00'

  const roundedSeconds = Math.ceil(timeInSeconds)
  const timeStamp = roundedSeconds * 1000
  const timeInMilliseconds = getTimeListInMilliseconds(timeStamp)
  const timeList = getTimeList(timeInMilliseconds)
  const [firstTime = 0, ...otherTimeList] = findTimeRange(timeList)

  return onlyMin && isEmpty(otherTimeList)
    ? '0'
    : isEmpty(otherTimeList)
      ? `0:${timeToString(firstTime)}`
      : onlyMin
        ? `${firstTime}`
        : `${firstTime}:${timeListToString(otherTimeList)}`
}

export const isValidIanaTimezone = (timeZone: string) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return true
  } catch (error) {
    logger.error('Argument is not valid IANA timezone')
    return false
  }
}

export const formatTimestampToLocaleString = (timestamp: string): string => {
  const date = new Date(timestamp)

  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export const getLocalTime = (timeZone: string) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(new Date())

export default formatTime
